
import { useQuery } from '@tanstack/react-query';
import { getFeatureFlagValue } from '@/services/featureFlagsService';
import { debugLogger } from '@/utils/debug';
import { useFeatureFlagsContext } from '@/contexts/featureFlags/FeatureFlagsContext';
import { FEATURE_FLAG_DEFAULTS, FeatureFlagKey } from '@/config/featureFlags';

/**
 * Hook to check if a feature flag is enabled in the database
 * @param flagKey The key of the feature flag to check
 * @returns boolean indicating if the feature is enabled
 */
const useFeatureFlag = (flagKey: FeatureFlagKey): boolean => {
  const ctx = useFeatureFlagsContext();
  
  // Always call useQuery to maintain hook order - disable when context available
  const { data: isEnabled } = useQuery({
    queryKey: ['featureFlag', flagKey],
    queryFn: async () => {
      debugLogger.log(`🔍 Querying feature flag: ${flagKey}`);
      try {
        const isEnabled = await getFeatureFlagValue(flagKey);
        debugLogger.log(`✅ Feature flag ${flagKey} value:`, isEnabled);
        return isEnabled;
      } catch (error) {
        debugLogger.error(`❌ Error fetching feature flag ${flagKey}:`, error);
        return FEATURE_FLAG_DEFAULTS[flagKey];
      }
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: !ctx, // Only run query when context is not available
  });
  
  // Return context value if available, otherwise query result
  if (ctx) return ctx.getFlag(flagKey);
  return !!isEnabled;
};

export default useFeatureFlag;
